/**
 * Screen-share surface geometry (customer side).
 *
 * The customer can share their entire monitor, an application window, or just
 * this browser tab. The agent needs two things to co-browse:
 *
 *   1. which surface it is — so the UI can say "Entire screen" and offer
 *      alignment tools instead of pretending the frame is the page; and
 *   2. where this page's viewport sits inside that surface — because remote
 *      control is dispatched as DOM events into *this* document, so agent
 *      pointer coords must be re-projected into viewport space before they
 *      can be used.
 *
 * Both are published over the signaling channel as SCREEN_SHARE_SURFACE.
 * A tab capture is the viewport by definition, hence FULL_REGION.
 */

export const FULL_REGION = Object.freeze({ x: 0, y: 0, w: 1, h: 1 });

const clamp01 = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.min(1, Math.max(0, v));
};

/** Map a MediaTrackSettings.displaySurface value onto our three cases. */
export function normalizeSurface(displaySurface) {
  return displaySurface === "monitor" || displaySurface === "window"
    ? displaySurface
    : "browser";
}

function toRegion(x, y, w, h) {
  const rx = clamp01(x);
  const ry = clamp01(y);
  const rw = Math.min(clamp01(w), 1 - rx);
  const rh = Math.min(clamp01(h), 1 - ry);
  if (rw <= 0 || rh <= 0) return FULL_REGION;
  return { x: rx, y: ry, w: rw, h: rh };
}

/**
 * Best-effort rectangle of this page's viewport inside the captured surface.
 *
 * The browser exposes the *outer* window position (screenX/screenY) but not
 * the viewport origin, so the chrome offsets are derived from
 * outer/inner deltas: side borders are (outerWidth - innerWidth) / 2 and the
 * top chrome is whatever vertical delta is left over.
 *
 * ponytail: heuristic. Docked DevTools and non-standard window decorations
 * shrink innerWidth/innerHeight without moving the viewport origin, so the
 * estimate skews. The agent can drag an exact box in the viewer ("Align") —
 * upgrade path is the Window Management API (getScreenDetails), which needs a
 * user permission grant we don't want to ask for mid-support-call.
 */
export function computeControlRegion(surface, win) {
  const w = win || (typeof window !== "undefined" ? window : null);
  if (!w) return FULL_REGION;
  if (normalizeSurface(surface) === "browser") return FULL_REGION;

  const innerW = Number(w.innerWidth) || 0;
  const innerH = Number(w.innerHeight) || 0;
  const outerW = Number(w.outerWidth) || 0;
  const outerH = Number(w.outerHeight) || 0;
  if (innerW <= 0 || innerH <= 0 || outerW <= 0 || outerH <= 0) return FULL_REGION;

  const sideBorder = Math.max(0, (outerW - innerW) / 2);
  const topChrome = Math.max(0, outerH - innerH - sideBorder);

  if (normalizeSurface(surface) === "window") {
    // Assume the shared window is this browser window — the only window we
    // can reason about. If it is some other app, the region is wrong and the
    // agent aligns manually.
    return toRegion(sideBorder / outerW, topChrome / outerH, innerW / outerW, innerH / outerH);
  }

  const screenW = Number(w.screen && w.screen.width) || 0;
  const screenH = Number(w.screen && w.screen.height) || 0;
  if (screenW <= 0 || screenH <= 0) return FULL_REGION;

  const originX = (Number(w.screenX) || 0) + sideBorder;
  const originY = (Number(w.screenY) || 0) + topChrome;
  return toRegion(originX / screenW, originY / screenH, innerW / screenW, innerH / screenH);
}

/** Cheap equality so the publisher only emits on real movement. */
export function regionsEqual(a, b, eps = 0.002) {
  if (!a || !b) return false;
  return (
    Math.abs(a.x - b.x) < eps &&
    Math.abs(a.y - b.y) < eps &&
    Math.abs(a.w - b.w) < eps &&
    Math.abs(a.h - b.h) < eps
  );
}
